import { useEffect, useState } from 'react';
import { App, Button, Card, Flex, Typography } from 'antd';
import { useGetAgreement } from '@/features/agreements/hooks/useAgreements';
import { ActionsDropdown } from '@/shared/components/ActionsDropdown';
import { SectionHeader } from '../SectionHeader';
import { EmptyStateCard } from '../EmptyStateCard';
import { LoadingIndicator } from '@/shared/components/LoadingIndicator';
import { ErrorMessage } from '@/shared/components/ErrorMessage';
import { PremiumCalculationDrawer } from './PremiumCalculationDrawer';
import { ExpressionSummaryDisplay } from './utils/ExpressionSummaryDisplay';

const { Text } = Typography;

export const CalculationsStep = ({ agreementId, onNextRef, readOnly = false }) => {
  const { notification } = App.useApp();
  const agreementQuery = useGetAgreement(agreementId);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingCalculation, setEditingCalculation] = useState(null);

  const agreement = agreementQuery.data;
  const calculations = agreement?.calculations ?? [];
  const premiumCalculation = calculations.find((c) => c.calculationType === 'premium');
  const hasPremiumCalculation = !!premiumCalculation;

  const handleNext = async () => {
    if (readOnly) return;
    if (!hasPremiumCalculation) {
      notification.error({
        message: <Text strong>Define el cálculo de prima</Text>,
        description: 'Es necesario configurar el cálculo de prima para continuar con la configuración del convenio.',
      });
      return false;
    }
  };

  useEffect(() => {
    if (!onNextRef) return;
    onNextRef.current = handleNext;
    return () => {
      onNextRef.current = null;
    };
  }, [handleNext, onNextRef, hasPremiumCalculation]);

  if (agreementQuery.isLoading) return <LoadingIndicator />;
  if (agreementQuery.isError) return <ErrorMessage error={agreementQuery.error} />;

  const handleOpenCreate = () => {
    setEditingCalculation(null);
    setDrawerOpen(true);
  };

  const handleOpenEdit = () => {
    setEditingCalculation(premiumCalculation);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setEditingCalculation(null);
  };

  const handleSave = () => {
    handleDrawerClose();
  };

  return (
    <Flex justify='center' style={{ paddingInline: '5%' }}>
      <Flex vertical gap='large' flex={1}>
        <SectionHeader
          title='Cálculo de prima'
          description='Define cómo se calcula la prima a partir del valor asegurado, tasa, IVA y operaciones.'
        />
        {hasPremiumCalculation ? (
          <Card
            size='small'
            style={{
              paddingInline: '3%',
              marginTop: -1,
              borderColor: '#d9d9d9',
              backgroundColor: 'transparent',
            }}
          >
            <Flex justify='space-between' align='center'>
              <Text><ExpressionSummaryDisplay node={premiumCalculation.expression} /></Text>
              {!readOnly && (
                <ActionsDropdown
                  items={[
                    {
                      key: 'edit',
                      label: 'Editar',
                      onClick: handleOpenEdit,
                    },
                  ]}
                />
              )}
            </Flex>
          </Card>
        ) : (
          <EmptyStateCard
            title='No hay cálculo de prima definido'
            description='Configura la fórmula que se usará para calcular la prima (por ejemplo: valor asegurado × tasa, más IVA).'
            action={
              !readOnly ? (
                <Button type='primary' onClick={handleOpenCreate}>
                  Definir cálculo de prima
                </Button>
              ) : null
            }
          />
        )}
        <PremiumCalculationDrawer
          open={drawerOpen}
          onClose={handleDrawerClose}
          agreementId={agreementId}
          calculation={editingCalculation}
          onSave={handleSave}
        />
      </Flex>
    </Flex>
  );
};
